import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { api, fetchWithAuth, Account, TaskWithContext, taskWithContextToProjectTask } from "@/lib/api";
import { format, parseISO, isBefore, startOfDay, isWithinInterval, addDays, endOfWeek } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/custom/SearchableSelect";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { TaskDetailRow } from "@/components/custom/TaskDetailRow";
import {
  Search,
  CheckSquare,
  Check,
  Loader2,
  ChevronRight,
  Plus,
} from "lucide-react";

interface User {
  id: string;
  name: string;
  email: string;
}

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  in_progress: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  on_hold: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  done: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
};

const statusLabels: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  done: "Done",
};

interface SummaryCardProps {
  count: number;
  label: string;
  onClick?: () => void;
  active?: boolean;
}

function SummaryCard({ count, label, onClick, active }: SummaryCardProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all min-w-[140px] text-left ${
        active 
          ? "bg-primary/10 border-primary shadow-sm" 
          : "bg-card border-border hover:bg-muted/50 hover:border-muted-foreground/30"
      }`}
      data-testid={`summary-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span className={`text-2xl font-bold ${active ? "text-primary" : "text-foreground"}`}>{count}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
      <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
    </button>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Filters — default: active projects only, my tasks only
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectStatusFilter, setProjectStatusFilter] = useState<"active" | "all">("active");
  const [assignedFilter, setAssignedFilter] = useState("my_tasks");
  const [accountFilter, setAccountFilter] = useState("all");
  const [dueFilter, setDueFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Scroll position save/restore
  useEffect(() => {
    const savedY = sessionStorage.getItem("tasks-scroll-y");
    if (savedY) {
      sessionStorage.removeItem("tasks-scroll-y");
      requestAnimationFrame(() => {
        document.getElementById("layout-scroll")?.scrollTo({ top: parseInt(savedY), behavior: "instant" as ScrollBehavior });
      });
    }
    return () => {
      const el = document.getElementById("layout-scroll");
      if (el && el.scrollTop > 0) {
        sessionStorage.setItem("tasks-scroll-y", String(el.scrollTop));
      }
    };
  }, []);

  // Create task modal state
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    project_id: "",
    title: "",
    due_date: "",
    assigned_user_id: "",
  });

  // Fetch tasks
  const { data: tasks = [], isLoading: tasksLoading } = useQuery<TaskWithContext[]>({
    queryKey: ["project-tasks", statusFilter, assignedFilter, accountFilter, searchQuery, projectStatusFilter, user?.id],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all" && statusFilter !== "pending") {
        params.append("status", statusFilter);
      }
      if (assignedFilter === "my_tasks" && user?.id) {
        params.append("assigned_user_id", user.id);
      } else if (assignedFilter === "unassigned") {
        params.append("assigned_user_id", "unassigned");
      } else if (assignedFilter !== "all") {
        params.append("assigned_user_id", assignedFilter);
      }
      if (accountFilter !== "all") params.append("account_id", accountFilter);
      if (searchQuery) params.append("search", searchQuery);
      // Closed projects are filtered on the server, before paging. Filtering them here
      // after a 1000-row cap left "All Assignees" showing a single client.
      if (projectStatusFilter === "active") params.append("active_projects", "1");
      
      const url = `/api/project-tasks${params.toString() ? `?${params.toString()}` : ""}`;
      return fetchWithAuth<TaskWithContext[]>(url);
    },
  });

  // Fetch users and accounts for filters
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => fetchWithAuth<User[]>("/api/users"),
  });

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(),
  });

  // Only id + status + title, for the Create Task picker: the full project list cost
  // ~10 s of server reads per visit (ENG-131).
  // Keyed under "projects" so the existing ["projects"] invalidations still refresh it.
  const { data: projects = [] } = useQuery<{ id: string; status: string; title: string }[]>({
    queryKey: ["projects", "statuses"],
    queryFn: () => api.getProjectStatuses(),
  });

  // Calculate summary counts
  const summaryStats = useMemo(() => {
    const today = startOfDay(new Date());
    const weekEnd = endOfWeek(today);
    const nextWeekEnd = addDays(weekEnd, 7);

    let dueToday = 0;
    let dueThisWeek = 0;
    let dueNextWeek = 0;
    let overdue = 0;

    tasks.forEach((task) => {
      if (task.status === "done" || !task.due_date) return;
      
      const dueDate = startOfDay(parseISO(task.due_date));
      
      if (isBefore(dueDate, today)) {
        overdue++;
      } else if (format(dueDate, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")) {
        dueToday++;
      } else if (isWithinInterval(dueDate, { start: today, end: weekEnd })) {
        dueThisWeek++;
      } else if (isWithinInterval(dueDate, { start: addDays(weekEnd, 1), end: nextWeekEnd })) {
        dueNextWeek++;
      }
    });

    return { dueToday, dueThisWeek, dueNextWeek, overdue };
  }, [tasks]);

  // Filter tasks based on status and due date filters (closed projects are filtered on the server)
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    if (statusFilter === "pending") {
      filtered = filtered.filter((t) => t.status !== "done");
    }

    if (dueFilter) {
      const today = startOfDay(new Date());
      const weekEnd = endOfWeek(today);
      const nextWeekEnd = addDays(weekEnd, 7);

      filtered = filtered.filter((task) => {
        if (!task.due_date) return false;
        const dueDate = startOfDay(parseISO(task.due_date));

        switch (dueFilter) {
          case "today":
            return format(dueDate, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
          case "this_week":
            return isWithinInterval(dueDate, { start: today, end: weekEnd }) && !isBefore(dueDate, today);
          case "next_week":
            return isWithinInterval(dueDate, { start: addDays(weekEnd, 1), end: nextWeekEnd });
          case "overdue":
            return isBefore(dueDate, today);
          default:
            return true;
        }
      });
    }

    // Demote blocked tasks to bottom within filter results
    return [...filtered].sort((a, b) => {
      const aBlocked = !!(a.is_blocked && a.predecessor_task_id && a.status !== "done");
      const bBlocked = !!(b.is_blocked && b.predecessor_task_id && b.status !== "done");
      if (aBlocked === bBlocked) return 0;
      return aBlocked ? 1 : -1;
    });
  }, [tasks, statusFilter, dueFilter]);

  // Two-level grouping: client → project → tasks, default all collapsed
  const groupedTasks = useMemo(() => {
    const accountMap = new Map<string, {
      account_id: string; account_name: string;
      projects: Map<string, { project_id: string; project_title: string; tasks: TaskWithContext[] }>;
    }>();
    for (const task of filteredTasks) {
      if (!accountMap.has(task.account_id)) {
        accountMap.set(task.account_id, { account_id: task.account_id, account_name: task.account_name, projects: new Map() });
      }
      const acc = accountMap.get(task.account_id)!;
      if (!acc.projects.has(task.project_id)) {
        acc.projects.set(task.project_id, { project_id: task.project_id, project_title: task.project_title, tasks: [] });
      }
      acc.projects.get(task.project_id)!.tasks.push(task);
    }
    return [...accountMap.values()]
      .sort((a, b) => a.account_name.localeCompare(b.account_name))
      .map((acc) => ({ ...acc, projects: [...acc.projects.values()].sort((a, b) => a.project_title.localeCompare(b.project_title)) }));
  }, [filteredTasks]);

  // Collapse state — default all collapsed when data first loads
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(new Set());
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && groupedTasks.length > 0) {
      initializedRef.current = true;
      setCollapsedAccounts(new Set(groupedTasks.map((g) => g.account_id)));
      setCollapsedProjects(new Set(groupedTasks.flatMap((g) => g.projects.map((p) => p.project_id))));
    }
  }, [groupedTasks]);

  const toggleAccount = (id: string) =>
    setCollapsedAccounts((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleProject = (id: string) =>
    setCollapsedProjects((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const createTaskMutation = useMutation({
    mutationFn: async () => {
      return fetchWithAuth(`/api/projects/${createForm.project_id}/tasks`, {
        method: "POST",
        body: JSON.stringify({
          title: createForm.title,
          due_date: createForm.due_date || null,
          assigned_user_id: createForm.assigned_user_id || null,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      setCreateTaskOpen(false);
      setCreateForm({ project_id: "", title: "", due_date: "", assigned_user_id: "" });
      toast({ title: "Task created" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create task", variant: "destructive" });
    },
  });

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header - Financial Cents style */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-[28px] font-heading font-bold leading-9 text-foreground">Tasks</h1>
            <p className="text-base text-muted-foreground mt-1">Manage tasks across all projects</p>
          </div>
          <Button className="bg-primary hover:bg-primary/90" onClick={() => setCreateTaskOpen(true)} data-testid="button-create-task">
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="flex gap-3 overflow-x-auto pb-1">
          <SummaryCard 
            count={summaryStats.dueToday} 
            label="Due Today" 
            onClick={() => setDueFilter(dueFilter === "today" ? null : "today")}
            active={dueFilter === "today"}
          />
          <SummaryCard 
            count={summaryStats.dueThisWeek} 
            label="Due This Week" 
            onClick={() => setDueFilter(dueFilter === "this_week" ? null : "this_week")}
            active={dueFilter === "this_week"}
          />
          <SummaryCard 
            count={summaryStats.dueNextWeek} 
            label="Due Next Week" 
            onClick={() => setDueFilter(dueFilter === "next_week" ? null : "next_week")}
            active={dueFilter === "next_week"}
          />
          <SummaryCard 
            count={summaryStats.overdue} 
            label="Overdue" 
            onClick={() => setDueFilter(dueFilter === "overdue" ? null : "overdue")}
            active={dueFilter === "overdue"}
          />
        </div>

        <Separator />

        {/* Filter Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
              data-testid="input-search-tasks"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-auto min-w-[120px] h-9" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>

          <SearchableSelect
            value={assignedFilter}
            onValueChange={setAssignedFilter}
            placeholder="Assignee"
            searchPlaceholder="Search assignees..."
            className="w-auto min-w-[130px]"
            data-testid="filter-assignee"
            options={[
              { value: "all", label: "All Assignees" },
              { value: "my_tasks", label: "My Tasks" },
              { value: "unassigned", label: "Unassigned" },
              ...users.filter((u) => u.id).map((u) => ({ value: u.id, label: u.name || u.email })),
            ]}
          />

          <SearchableSelect
            value={accountFilter}
            onValueChange={setAccountFilter}
            placeholder="Client"
            searchPlaceholder="Search clients..."
            className="w-auto min-w-[120px]"
            data-testid="filter-clients"
            options={[
              { value: "all", label: "All Clients" },
              ...accounts.filter((a) => a.id).map((a) => ({ value: a.id, label: a.account_name })),
            ]}
          />

          <Button
            variant={assignedFilter === "my_tasks" ? "default" : "outline"}
            size="sm"
            className={`h-9 text-xs ${assignedFilter === "my_tasks" ? "bg-violet-600 hover:bg-violet-700 border-violet-600" : ""}`}
            onClick={() => setAssignedFilter((v) => v === "my_tasks" ? "all" : "my_tasks")}
            title="Show only tasks assigned to you"
          >
            My Tasks
          </Button>

          {/* Project status toggle */}
          <Button
            variant={projectStatusFilter === "active" ? "default" : "outline"}
            size="sm"
            className="h-9 text-xs"
            onClick={() => setProjectStatusFilter((v) => v === "active" ? "all" : "active")}
          >
            {projectStatusFilter === "active" ? "Active Projects" : "All Projects"}
          </Button>
        </div>

        {/* Tasks Table - Financial Cents style */}
        {tasksLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="border rounded-lg bg-card">
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CheckSquare className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No tasks found</p>
              <p className="text-sm">Adjust your filters or create tasks from a project</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {groupedTasks.map((account) => {
              const accountCollapsed = collapsedAccounts.has(account.account_id);
              const accountTotal = account.projects.reduce((s, p) => s + p.tasks.length, 0);
              const accountOpen = account.projects.reduce((s, p) => s + p.tasks.filter((t) => t.status !== "done").length, 0);
              return (
                <div key={account.account_id} className="border rounded-lg overflow-hidden bg-card">
                  {/* Client header */}
                  <button
                    onClick={() => toggleAccount(account.account_id)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted/70 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ChevronRight className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-150 ${accountCollapsed ? "" : "rotate-90"}`} />
                      <span className="font-bold text-sm">{account.account_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-3">
                      {accountOpen > 0 && <Badge variant="secondary" className="text-xs">{accountOpen} open</Badge>}
                      <Badge variant="outline" className="text-xs">{accountTotal} tasks</Badge>
                    </div>
                  </button>

                  {/* Projects */}
                  {!accountCollapsed && account.projects.map((project) => {
                    const projCollapsed = collapsedProjects.has(project.project_id);
                    const projOpen = project.tasks.filter((t) => t.status !== "done").length;
                    return (
                      <div key={project.project_id} className="border-t">
                        {/* Project sub-header */}
                        <div className="flex items-center pl-8 pr-4 py-2 bg-indigo-50 border-b border-indigo-100 hover:bg-indigo-100/70 transition-colors gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const allDone = project.tasks.every((t) => t.status === "done");
                              fetchWithAuth(`/api/projects/${project.project_id}`, {
                                method: "PUT",
                                body: JSON.stringify({ status: allDone ? "open" : "closed" }),
                              });
                            }}
                            className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${project.tasks.every((t) => t.status === "done") ? "bg-green-500 border-green-500 hover:bg-green-600" : "border-indigo-300 hover:border-green-400"}`}
                            title="Mark project complete"
                          >
                            {project.tasks.every((t) => t.status === "done") && <Check className="w-2.5 h-2.5 text-white" />}
                          </button>
                          <button
                            onClick={() => toggleProject(project.project_id)}
                            className="flex items-center gap-2 flex-1 text-left min-w-0"
                          >
                            <ChevronRight className={`w-3.5 h-3.5 text-indigo-400 shrink-0 transition-transform duration-150 ${projCollapsed ? "" : "rotate-90"}`} />
                            <span className="text-sm font-bold text-indigo-900">{project.project_title}</span>
                            <span className="text-xs font-bold text-indigo-800 bg-indigo-100 border border-indigo-200 rounded-full px-2 py-0.5 shrink-0">
                              {projOpen}/{project.tasks.length}
                            </span>
                          </button>
                        </div>

                        {/* Task rows */}
                        {!projCollapsed && (
                          <div className="pl-8">
                            {project.tasks.map((task) => (
                              <TaskDetailRow
                                key={task.id}
                                task={taskWithContextToProjectTask(task)}
                                accountId={task.account_id}
                                onStatusChange={(id, done) => {
                                  queryClient.setQueryData<TaskWithContext[]>(
                                    ["project-tasks", statusFilter, assignedFilter, accountFilter, searchQuery, user?.id],
                                    (old) => old ? old.map((t) => t.id === id ? { ...t, status: done ? "done" : "open" } : t) : old
                                  );
                                }}
                                onDelete={(id) => {
                                  queryClient.setQueryData<TaskWithContext[]>(
                                    ["project-tasks", statusFilter, assignedFilter, accountFilter, searchQuery, user?.id],
                                    (old) => old ? old.filter((t) => t.id !== id) : old
                                  );
                                  queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
                                }}
                                onUpdate={() => {
                                  queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Create Task Modal */}
        <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Task</DialogTitle>
              <DialogDescription>Add a new task to a project.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="create-project">Project *</Label>
                <Select value={createForm.project_id} onValueChange={(v) => setCreateForm({ ...createForm, project_id: v })}>
                  <SelectTrigger id="create-project" data-testid="select-create-project">
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-title">Task Title *</Label>
                <Input
                  id="create-title"
                  placeholder="What needs to be done?"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  data-testid="input-create-title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-due">Due Date</Label>
                <Input
                  id="create-due"
                  type="date"
                  value={createForm.due_date}
                  onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })}
                  data-testid="input-create-due"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-assignee">Assign To</Label>
                <Select value={createForm.assigned_user_id} onValueChange={(v) => setCreateForm({ ...createForm, assigned_user_id: v === "none" ? "" : v })}>
                  <SelectTrigger id="create-assignee" data-testid="select-create-assignee">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateTaskOpen(false)}>Cancel</Button>
              <Button
                onClick={() => createTaskMutation.mutate()}
                disabled={!createForm.project_id || !createForm.title.trim() || createTaskMutation.isPending}
                data-testid="button-submit-create-task"
              >
                {createTaskMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
}
